import React, { useState } from 'react';
import { Plus, Trash2, Edit2, Check, X } from 'lucide-react';

const SubmitterRolesManager = ({
  roles,
  activeRoleUuid,
  onSetActiveRole,
  onAddRole,
  onUpdateRole,
  onRemoveRole,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingRoleUuid, setEditingRoleUuid] = useState(null);
  const [inputValue, setInputValue] = useState('');

  const handleStartAdd = () => {
    setIsAdding(true);
    setInputValue('');
  };

  const handleCancelAdd = () => {
    setIsAdding(false);
    setInputValue('');
  };

  const handleConfirmAdd = () => {
    if (inputValue.trim()) {
      onAddRole(inputValue.trim());
      setIsAdding(false);
      setInputValue('');
    }
  };

  const handleStartEdit = (role) => {
    setEditingRoleUuid(role.uuid);
    setInputValue(role.name);
  };

  const handleCancelEdit = () => {
    setEditingRoleUuid(null);
    setInputValue('');
  };

  const handleConfirmEdit = () => {
    if (inputValue.trim() && editingRoleUuid) {
      onUpdateRole(editingRoleUuid, { name: inputValue.trim() });
      setEditingRoleUuid(null);
      setInputValue('');
    }
  };

  const handleKeyDown = (e, action) => {
    if (e.key === 'Enter') {
      action();
    } else if (e.key === 'Escape') {
      if (isAdding) handleCancelAdd();
      if (editingRoleUuid) handleCancelEdit();
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">Signer Roles</h3>
        {!isAdding && (
          <button
            onClick={handleStartAdd}
            className="p-1 text-gray-500 hover:text-yellow-600 hover:bg-yellow-50 rounded transition-colors"
            title="Add role"
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>

      <p className="text-xs text-gray-500 mb-4">
        Select a role before adding fields
      </p>

      <div className="space-y-2">
        {roles.map((role) => (
          <div
            key={role.uuid}
            className={`
              flex items-center gap-2 p-2 rounded-lg border-2 transition-all
              ${activeRoleUuid === role.uuid
                ? 'border-yellow-400 bg-yellow-50'
                : 'border-gray-200 hover:border-gray-300'
              }
            `}
          >
            {editingRoleUuid === role.uuid ? (
              // Edit mode
              <div className="flex-1 flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: role.color }}
                />
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, handleConfirmEdit)}
                  className="flex-1 text-sm px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500"
                  autoFocus
                />
                <button
                  onClick={handleConfirmEdit}
                  className="p-1 text-green-600 hover:bg-green-50 rounded"
                  title="Save"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                  title="Cancel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              // Display mode
              <>
                <button
                  onClick={() => onSetActiveRole(role.uuid)}
                  className="flex-1 flex items-center gap-2 text-left"
                >
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: role.color }}
                  />
                  <span className="text-sm font-medium text-gray-700 truncate">
                    {role.name}
                  </span>
                  {activeRoleUuid === role.uuid && (
                    <span className="text-xs text-yellow-600 font-medium">
                      (active)
                    </span>
                  )}
                </button>
                <button
                  onClick={() => handleStartEdit(role)}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Edit role"
                >
                  <Edit2 className="w-3 h-3" />
                </button>
                {roles.length > 1 && (
                  <button
                    onClick={() => onRemoveRole(role.uuid)}
                    className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                    title="Remove role"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </>
            )}
          </div>
        ))}

        {/* Add new role input */}
        {isAdding && (
          <div className="flex items-center gap-2 p-2 rounded-lg border-2 border-dashed border-gray-300">
            <div className="w-3 h-3 rounded-full bg-gray-300 flex-shrink-0" />
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, handleConfirmAdd)}
              placeholder="Role name..."
              className="flex-1 text-sm px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500"
              autoFocus
            />
            <button
              onClick={handleConfirmAdd}
              disabled={!inputValue.trim()}
              className="p-1 text-green-600 hover:bg-green-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              title="Add"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              onClick={handleCancelAdd}
              className="p-1 text-gray-500 hover:bg-gray-100 rounded"
              title="Cancel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmitterRolesManager;
